import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IndiaSplash from "./IndiaSplash";
import globePointsRaw from "../data/globe-points.json?raw";
import { REGIONS, REGION_STORAGE_KEY, setRegion } from "../utils/region";
import "../styles/region-picker.css";

const POINTS = JSON.parse(globePointsRaw);

const CONFIG = {
  india: { url: "/in", name: "Praesidio Studio", place: "India" },
  world: { url: "/", name: "Praesidio Care", place: "Rest of the world" },
};

const D2R = Math.PI / 180;

/* Points pre-computation: [lat*10, lon*10, flag], flag 2 = India */
const P = POINTS.map(([a, o, f]) => {
  const la = (a / 10) * D2R;
  const lo = (o / 10) * D2R;
  return {
    cl: Math.cos(la),
    sl: Math.sin(la),
    lo,
    f,
    lat: a / 10,
    lon: o / 10,
  };
});

/* 3° spatial hash so hover/click doesn't scan every land dot */
const CELL = 3;
const GRID = new Map();
for (const p of P) {
  const key = `${Math.round(p.lat / CELL)}|${Math.round(p.lon / CELL)}`;
  const bucket = GRID.get(key);
  if (bucket) bucket.push(p);
  else GRID.set(key, [p]);
}

const CITIES = [
  { n: "", lat: 12.97, lon: 77.59, dx: 12, dy: 4 },
  { n: "", lat: 19.99, lon: 73.79, dx: -12, dy: 4, right: true },
  { n: "", lat: 23.02, lon: 72.57, dx: -12, dy: -4, right: true },
];

const ORIGIN = { lat: 19.99, lon: 73.79 };

const DEST = [
  [51.5, -0.12],
  [40.7, -74],
  [37.77, -122.4],
  [52.5, 13.4],
  [25.2, 55.3],
  [1.35, 103.8],
  [-33.9, 151.2],
  [43.65, -79.4],
  [35.7, 139.7],
  [-23.5, -46.6],
  [-1.29, 36.8],
  [48.85, 2.35],
];

function vec(lat, lon) {
  lat *= D2R;
  lon *= D2R;
  return [
    Math.cos(lat) * Math.cos(lon),
    Math.cos(lat) * Math.sin(lon),
    Math.sin(lat),
  ];
}

const ARCS = DEST.map(([la, lo], i) => {
  const A = vec(ORIGIN.lat, ORIGIN.lon);
  const B = vec(la, lo);
  const om = Math.acos(
    Math.min(1, Math.max(-1, A[0] * B[0] + A[1] * B[1] + A[2] * B[2]))
  );
  const so = Math.sin(om) || 0.0001;
  const pts = [];
  for (let k = 0; k <= 48; k++) {
    const t = k / 48;
    const f1 = Math.sin((1 - t) * om) / so;
    const f2 = Math.sin(t * om) / so;
    const v = [
      A[0] * f1 + B[0] * f2,
      A[1] * f1 + B[1] * f2,
      A[2] * f1 + B[2] * f2,
    ];
    const lat = Math.asin(Math.max(-1, Math.min(1, v[2])));
    const lon = Math.atan2(v[1], v[0]);
    pts.push({
      cl: Math.cos(lat),
      sl: Math.sin(lat),
      lo: lon,
      lift: 1 + 0.06 * om * Math.sin(Math.PI * t),
    });
  }
  return { pts, delay: i * 0.13 };
});

const VIEWS = {
  idle: { lon: 40, lat: 18, s: 1 },
  india: { lon: 78, lat: 21, s: 1.65 },
  world: { lon: 30, lat: 22, s: 1 },
};

function hexToRgb(h) {
  h = (h || "").trim().replace("#", "");
  if (h.length === 3) h = [...h].map((c) => c + c).join("");
  const n = parseInt(h, 16);
  if (isNaN(n)) return [200, 243, 29];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const easeVal = (c, t, k) => c + (t - c) * k;

export default function RegionGate() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const hintRef = useRef(null);
  const readoutRef = useRef(null);
  const roNameRef = useRef(null);
  const roCoordRef = useRef(null);
  const curtainRef = useRef(null);
  const cuTitleRef = useRef(null);
  const cuSubRef = useRef(null);
  const indiaChoiceRef = useRef(null);
  const worldChoiceRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [showSplash, setShowSplash] = useState(false);

  const handleIndiaComplete = () => {
    document.documentElement.classList.remove("region-gate-active");
    document.documentElement.classList.remove("india-splash-active");
    // Hide any existing loader from main app
    const loader = document.getElementById("loader");
    if (loader) {
      loader.style.display = "none";
    }
    window.location.assign("/in");
  };

  const [suggestion, setSuggestion] = useState(() => {
    let last = null;
    try {
      last = localStorage.getItem(REGION_STORAGE_KEY);
    } catch {
      /* private mode */
    }
    if (last === "india" || last === "in") {
      return { region: "india", label: "Last visited" };
    }
    if (last === "world" || last === "row") {
      return { region: "world", label: "Last visited" };
    }
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (/Asia\/(Kolkata|Calcutta)/.test(tz)) {
        return { region: "india", label: "Suggested" };
      }
      if (tz) {
        return { region: "world", label: "Suggested" };
      }
    } catch {
      /* ignore */
    }
    return null;
  });

  useEffect(() => {
    document.documentElement.classList.add("region-gate-active");
    const rafReady = requestAnimationFrame(() => {
      setIsReady(true);
    });

    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* Theme colors */
    const C = {
      lime: [200, 243, 29],
      dot: [110, 110, 118],
      ink: [244, 244, 239],
      dark: true,
      ramp: [],
    };

    function readTheme() {
      const el = rootRef.current || document.documentElement;
      const cs = getComputedStyle(el);
      const limeStr = cs.getPropertyValue("--lime");
      const dotStr = cs.getPropertyValue("--dot");
      const inkStr = cs.getPropertyValue("--ink");
      if (limeStr) C.lime = hexToRgb(limeStr);
      if (dotStr) C.dot = hexToRgb(dotStr);
      if (inkStr) C.ink = hexToRgb(inkStr);
      C.dark = cs.getPropertyValue("color-scheme").trim() !== "light";
      C.ramp = [];
      for (let i = 0; i <= 10; i++) {
        const t = i / 10;
        C.ramp.push(
          `rgb(${C.dot.map((v, k) => Math.round(v + (C.lime[k] - v) * t)).join(",")})`
        );
      }
    }
    readTheme();

    const mqlTheme = window.matchMedia("(prefers-color-scheme: light)");
    const onThemeChange = () => readTheme();
    if (mqlTheme.addEventListener) {
      mqlTheme.addEventListener("change", onThemeChange);
    } else if (mqlTheme.addListener) {
      mqlTheme.addListener(onThemeChange);
    }

    /* State */
    const st = {
      lon: -60,
      lat: 10,
      s: 0.82,
      tLon: 40,
      tLat: 18,
      tS: 1,
      iI: 0,
      iW: 0,
      mode: "idle",
      drag: null,
      userSpun: false,
      hoverGlobe: null,
      leaving: false,
      t0: performance.now(),
      alpha: 0,
    };

    let W = 0;
    let H = 0;
    let DPR = 1;
    let cx = 0;
    let cy = 0;
    let R0 = 0;

    function resize() {
      if (!stage || !canvas) return;
      const r = stage.getBoundingClientRect();
      DPR = Math.min(window.devicePixelRatio || 1, 1.5);
      W = r.width;
      H = r.height;
      if (W === 0 || H === 0) return;
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      const mobile = W < 520 || window.innerWidth <= 860;
      R0 = mobile ? Math.min(W * 0.42, H * 0.44) : Math.min(W * 0.4, H * 0.4);
      cx = W / 2;
      cy = H / 2 + (mobile ? 8 : 0);
    }
    resize();
    window.addEventListener("resize", resize);

    const resizeObserver = new ResizeObserver(() => {
      resize();
    });
    resizeObserver.observe(stage);

    /* Projection */
    const tmp = { x: 0, y: 0, z: 0 };
    function project(cl, sl, lo, out, lift = 1) {
      const lam = lo - st.lon * D2R;
      const a = st.lat * D2R;
      const x = cl * Math.sin(lam);
      const y = sl;
      const z = cl * Math.cos(lam);
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      out.x = x * lift;
      out.y = (y * ca - z * sa) * lift;
      out.z = (y * sa + z * ca) * lift;
      return out;
    }

    function unproject(mx, my) {
      const R = R0 * st.s;
      const nx = (mx - cx) / R;
      const ny = -(my - cy) / R;
      const d = nx * nx + ny * ny;
      if (d > 1) return null;
      const z1 = Math.sqrt(1 - d);
      const a = st.lat * D2R;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const y = ny * ca + z1 * sa;
      const z = -ny * sa + z1 * ca;
      let lon = Math.atan2(nx, z) / D2R + st.lon;
      lon = ((lon + 540) % 360) - 180;
      return {
        lat: Math.asin(Math.max(-1, Math.min(1, y))) / D2R,
        lon,
      };
    }

    function regionAt(ll) {
      if (!ll) return null;
      let best = null;
      let bd = 4;
      const gx = Math.round(ll.lat / CELL);
      const gy = Math.round(ll.lon / CELL);
      const cosLat = Math.cos(ll.lat * D2R);
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const bucket = GRID.get(`${gx + dy}|${gy + dx}`);
          if (!bucket) continue;
          for (const p of bucket) {
            let dlo = Math.abs(p.lon - ll.lon);
            if (dlo > 180) dlo = 360 - dlo;
            const d = Math.hypot(p.lat - ll.lat, dlo * cosLat);
            if (d < bd) {
              bd = d;
              best = p;
            }
          }
        }
      }
      if (!best || bd > 1.6) return "world"; // ocean counts as the world
      return best.f === 2 ? "india" : "world";
    }

    /* Readout */
    let lastRo = "";
    function updateReadout() {
      const lon = ((st.lon + 540) % 360) - 180;
      const lat = st.lat;
      const s = `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? "N" : "S"}, ${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? "E" : "W"}`;
      const nm =
        st.mode === "india"
          ? "Praesidio Studio, India"
          : st.mode === "world"
          ? "Praesidio Care, worldwide"
          : "Where are you?";
      const key = s + nm;
      if (key === lastRo) return;
      lastRo = key;
      if (roCoordRef.current) roCoordRef.current.textContent = s;
      if (roNameRef.current) roNameRef.current.textContent = nm;
      if (readoutRef.current) readoutRef.current.classList.toggle("lit", st.mode !== "idle");
    }

    /* Mode & Selection */
    let pinned = null;
    let idleTimer = null;
    const timers = [];

    function syncChoiceClasses(mode) {
      if (indiaChoiceRef.current) {
        indiaChoiceRef.current.classList.toggle("is-active", mode === "india");
      }
      if (worldChoiceRef.current) {
        worldChoiceRef.current.classList.toggle("is-active", mode === "world");
      }
    }

    function setMode(m) {
      clearTimeout(idleTimer);
      st.mode = m;
      const v = VIEWS[m];
      if (m !== "idle" || !st.userSpun) {
        st.tLon = v.lon;
        st.tLat = v.lat;
      }
      if (m === "idle") {
        st.tLat = VIEWS.idle.lat;
        st.tLon = st.lon;
      }
      st.tS = v.s;
      syncChoiceClasses(m);
    }

    function relax() {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (!st.leaving) setMode(pinned || "idle");
      }, 900);
    }

    function select(region) {
      if (st.leaving) return;
      st.leaving = true;

      if (region === "india") {
        setMode("india");
        st.tS = 3.2;
        setRegion(REGIONS.IN);
        try {
          localStorage.setItem(REGION_STORAGE_KEY, REGIONS.IN);
        } catch {
          /* private mode */
        }
        // Navigate to splash screen first, then to /in
        window.location.assign("/entering-in");
        return;
      }

      setMode(region);
      st.tS = 2.2;
      const cfg = CONFIG[region];

      if (cuTitleRef.current) cuTitleRef.current.textContent = cfg.name;
      if (cuSubRef.current) cuSubRef.current.textContent = "Taking you there";

      const t1 = setTimeout(() => {
        if (curtainRef.current) curtainRef.current.classList.add("show");
      }, reduced ? 0 : 650);

      const t2 = setTimeout(() => {
        setRegion(REGIONS.ROW);
        try {
          localStorage.setItem(REGION_STORAGE_KEY, REGIONS.ROW);
        } catch {
          /* private mode */
        }
        document.documentElement.classList.remove("region-gate-active");
        navigate(cfg.url);
      }, reduced ? 50 : 1700);

      timers.push(t1, t2);
    }

    /* Animation Frame */
    let rafId = null;
    let splashGone = false;
    const nPts = P.length;
    const dotX = new Float32Array(nPts);
    const dotY = new Float32Array(nPts);
    const dotS = new Float32Array(nPts);
    const BANDS = 8;
    const GROUPS = 11 * BANDS;
    const head = new Int32Array(GROUPS);
    const nextIdx = new Int32Array(nPts);
    const farX = new Float32Array(nPts);
    const farY = new Float32Array(nPts);
    const alphaOf = [0, 0.14, 0.28, 0.43, 0.57, 0.71, 0.86, 1];

    function frame(now) {
      if (document.hidden) return;
      if (!splashGone) {
        const ld = document.getElementById("loader");
        splashGone = !ld || ld.style.display === "none";
        if (!splashGone) {
          rafId = requestAnimationFrame(frame);
          return;
        }
      }
      const t = (now - st.t0) / 1000;
      const k = reduced ? 1 : 0.07;
      if (!st.drag) {
        if (st.mode === "idle" && !st.userSpun && !reduced) {
          st.tLon += 0.035;
        }
        const dl = ((st.tLon - st.lon + 540) % 360) - 180;
        st.lon += dl * k;
        st.lat = easeVal(st.lat, st.tLat, k);
      }
      st.s = easeVal(st.s, st.tS, reduced ? 1 : 0.06);
      st.iI = easeVal(st.iI, st.mode === "india" ? 1 : 0, reduced ? 1 : 0.08);
      st.iW = easeVal(st.iW, st.mode === "world" ? 1 : 0, reduced ? 1 : 0.08);
      st.alpha = easeVal(st.alpha, 1, reduced ? 1 : 0.03);

      ctx.clearRect(0, 0, W, H);
      const R = R0 * st.s;
      const A = st.alpha;
      const lit = Math.max(st.iI, st.iW);

      // Atmosphere
      const g = ctx.createRadialGradient(cx, cy, R * 0.8, cx, cy, R * 1.35);
      g.addColorStop(0, rgba(C.lime, (C.dark ? 0.07 : 0.05) + 0.07 * lit));
      g.addColorStop(1, rgba(C.lime, 0));
      ctx.globalAlpha = A;
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.35, 0, 7);
      ctx.fill();

      const body = ctx.createRadialGradient(
        cx - R * 0.35,
        cy - R * 0.4,
        R * 0.1,
        cx,
        cy,
        R
      );
      body.addColorStop(0, rgba(C.ink, C.dark ? 0.06 : 0.05));
      body.addColorStop(1, rgba(C.ink, C.dark ? 0.012 : 0.01));
      ctx.fillStyle = body;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, 7);
      ctx.fill();
      ctx.strokeStyle = rgba(C.ink, 0.09);
      ctx.lineWidth = 1;
      ctx.stroke();

      // Dots — one fill per color/alpha band, not an arc per land point
      const base = Math.max(0.8, R / 290);
      head.fill(-1);
      let farN = 0;
      let nearN = 0;
      for (let i = 0; i < nPts; i++) {
        const p = P[i];
        project(p.cl, p.sl, p.lo, tmp);
        const sx = cx + tmp.x * R;
        const sy = cy - tmp.y * R;
        if (tmp.z < 0) {
          farX[farN] = sx;
          farY[farN] = sy;
          farN++;
          continue;
        }
        const isI = p.f === 2;
        let w = isI ? 0.45 + 0.55 * st.iI - 0.2 * st.iW : 0.78 * st.iW;
        let a = 0.28 + 0.72 * tmp.z;
        if (isI) a *= 1 - 0.3 * st.iW;
        else a *= 1 - 0.55 * st.iI;
        if (st.hoverGlobe && !st.drag) {
          if ((st.hoverGlobe === "india") === isI) a = Math.min(1, a * 1.15);
        }
        const r = base * (0.55 + 0.6 * tmp.z) * (isI ? 1 + 0.35 * st.iI : 1);
        const ci = Math.max(0, Math.min(10, Math.round(w * 10)));
        const band = Math.max(0, Math.min(BANDS - 1, Math.round(a * (BANDS - 1))));
        const g = ci * BANDS + band;
        dotX[nearN] = sx - r;
        dotY[nearN] = sy - r;
        dotS[nearN] = r * 2;
        nextIdx[nearN] = head[g];
        head[g] = nearN;
        nearN++;
      }
      if (farN) {
        ctx.globalAlpha = A * 0.045;
        ctx.fillStyle = C.ramp[0];
        ctx.beginPath();
        for (let i = 0; i < farN; i++) ctx.rect(farX[i], farY[i], 1.1, 1.1);
        ctx.fill();
      }
      for (let g = 0; g < GROUPS; g++) {
        let i = head[g];
        if (i === -1) continue;
        ctx.globalAlpha = A * alphaOf[g % BANDS];
        ctx.fillStyle = C.ramp[(g / BANDS) | 0];
        ctx.beginPath();
        while (i !== -1) {
          ctx.rect(dotX[i], dotY[i], dotS[i], dotS[i]);
          i = nextIdx[i];
        }
        ctx.fill();
      }

      // Arcs to the world
      if (st.iW > 0.02) {
        ctx.lineWidth = 1.3;
        ctx.lineCap = "round";
        for (const arc of ARCS) {
          const cyc = (((t * 0.35 - arc.delay) % 1.6) + 1.6) % 1.6;
          const head = Math.min(1, cyc / 1);
          const tail = Math.max(0, (cyc - 0.55) / 1.05);
          const n = arc.pts.length - 1;
          const i0 = Math.floor(tail * n);
          const i1 = Math.floor(head * n);
          ctx.beginPath();
          let pen = false;
          for (let i = i0; i <= i1; i++) {
            const q = arc.pts[i];
            project(q.cl, q.sl, q.lo, tmp, q.lift);
            const vis = tmp.z > 0 || tmp.x * tmp.x + tmp.y * tmp.y > 1;
            const sx = cx + tmp.x * R;
            const sy = cy - tmp.y * R;
            if (vis) {
              if (pen) ctx.lineTo(sx, sy);
              else ctx.moveTo(sx, sy);
              pen = true;
            } else {
              pen = false;
            }
          }
          ctx.globalAlpha = A * st.iW * 0.85;
          ctx.strokeStyle = rgba(C.lime, 1);
          ctx.stroke();

          if (head >= 1 && cyc < 1.4) {
            const q = arc.pts[n];
            project(q.cl, q.sl, q.lo, tmp);
            if (tmp.z > 0) {
              const pr = (cyc - 1) / 0.4;
              ctx.globalAlpha = A * st.iW * (1 - pr);
              ctx.beginPath();
              ctx.arc(cx + tmp.x * R, cy - tmp.y * R, 2 + pr * 10, 0, 7);
              ctx.stroke();
              ctx.globalAlpha = A * st.iW;
              ctx.fillStyle = rgba(C.lime, 1);
              ctx.beginPath();
              ctx.arc(cx + tmp.x * R, cy - tmp.y * R, 2, 0, 7);
              ctx.fill();
            }
          }
        }
      }

      // Indian cities
      if (st.iI > 0.02) {
        ctx.font = `500 ${Math.round(Math.max(12, R / 26))}px Outfit, system-ui, sans-serif`;
        ctx.textBaseline = "middle";
        CITIES.forEach((c, i) => {
          project(
            Math.cos(c.lat * D2R),
            Math.sin(c.lat * D2R),
            c.lon * D2R,
            tmp
          );
          if (tmp.z <= 0) return;
          const sx = cx + tmp.x * R;
          const sy = cy - tmp.y * R;
          const ph = ((t * 0.8 + i * 0.33) % 1);
          ctx.globalAlpha = st.iI * (1 - ph);
          ctx.strokeStyle = rgba(C.lime, 1);
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(sx, sy, 4 + ph * 16, 0, 7);
          ctx.stroke();

          ctx.globalAlpha = st.iI;
          ctx.fillStyle = rgba(C.ink, 1);
          ctx.beginPath();
          ctx.arc(sx, sy, 3.6, 0, 7);
          ctx.fill();

          ctx.fillStyle = rgba(C.lime, 1);
          ctx.beginPath();
          ctx.arc(sx, sy, 2, 0, 7);
          ctx.fill();

          ctx.fillStyle = rgba(C.ink, 1);
          ctx.textAlign = c.right ? "right" : "left";
          ctx.fillText(c.n, sx + c.dx * (R / 300 + 0.6), sy + c.dy);
        });
      }

      ctx.globalAlpha = 1;
      updateReadout();
      rafId = requestAnimationFrame(frame);
    }
    rafId = requestAnimationFrame(frame);

    const onVis = () => {
      if (!document.hidden && !rafId) rafId = requestAnimationFrame(frame);
    };
    document.addEventListener("visibilitychange", onVis);

    /* Pointer drag on canvas */
    function pos(e) {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    const onPointerDown = (e) => {
      const p = pos(e);
      st.drag = {
        x: p.x,
        y: p.y,
        lon: st.lon,
        lat: st.lat,
        moved: false,
        id: e.pointerId,
      };
    };

    const onPointerMove = (e) => {
      const p = pos(e);
      if (st.drag) {
        const dx = p.x - st.drag.x;
        const dy = p.y - st.drag.y;
        if (!st.drag.moved && Math.hypot(dx, dy) > 5) {
          st.drag.moved = true;
          try {
            canvas.setPointerCapture(st.drag.id);
          } catch {
            /* ignore pointer capture failure */
          }
          canvas.classList.add("grabbing");
          if (hintRef.current) hintRef.current.classList.add("gone");
        }
        if (st.drag.moved) {
          const f = 0.28 / st.s;
          st.lon = st.drag.lon - dx * f;
          st.lon = ((st.lon + 540) % 360) - 180;
          if (e.pointerType === "mouse") {
            st.lat = Math.max(-60, Math.min(70, st.drag.lat + dy * f));
          }
          st.tLon = st.lon;
          st.tLat = st.lat;
          st.userSpun = true;
        }
        return;
      }
      if (st.leaving) return;
      const reg = regionAt(unproject(p.x, p.y));
      st.hoverGlobe = reg;
      canvas.classList.toggle("pointing", !!reg);
      if (reg && e.pointerType === "mouse") {
        clearTimeout(idleTimer);
        setMode(reg);
        st.tS = reg === "india" ? Math.max(st.tS, 1.15) : 1;
      } else if (!reg && st.mode !== "idle" && !pinned) {
        relax();
      }
    };

    const onPointerUp = (e) => {
      if (!st.drag) return;
      const moved = st.drag.moved;
      st.drag = null;
      canvas.classList.remove("grabbing");
      if (!moved) {
        const p = pos(e);
        const reg = regionAt(unproject(p.x, p.y));
        if (reg) {
          if (e.pointerType !== "mouse" && st.mode !== reg) {
            setMode(reg);
            pinned = reg;
          } else {
            select(reg);
          }
        }
      }
    };

    const onPointerCancel = () => {
      st.drag = null;
      canvas.classList.remove("grabbing");
    };

    const onPointerLeave = () => {
      st.hoverGlobe = null;
      if (!st.drag && !st.leaving) relax();
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerCancel);
    canvas.addEventListener("pointerleave", onPointerLeave);

    /* Keyboard navigation */
    const onWindowKey = (e) => {
      if (e.target.closest && e.target.closest("button, input, textarea")) return;
      if (e.key === "1") select("india");
      if (e.key === "2") select("world");
    };
    window.addEventListener("keydown", onWindowKey);

    /* Choice buttons listeners */
    const choiceEls = [indiaChoiceRef.current, worldChoiceRef.current].filter(Boolean);
    const choiceCleanups = [];

    choiceEls.forEach((c) => {
      const r = c.dataset.region;
      const onEnter = () => setMode(r);
      const onLeave = () => relax();
      const onFocus = () => setMode(r);
      const onBlur = () => relax();
      const onClick = (e) => {
        e.preventDefault();
        select(r);
      };
      const onKey = (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          const i = choiceEls.indexOf(c);
          const nextIdx = (i + (e.key === "ArrowDown" ? 1 : -1) + choiceEls.length) % choiceEls.length;
          choiceEls[nextIdx].focus();
        }
      };

      c.addEventListener("mouseenter", onEnter);
      c.addEventListener("mouseleave", onLeave);
      c.addEventListener("focus", onFocus);
      c.addEventListener("blur", onBlur);
      c.addEventListener("click", onClick);
      c.addEventListener("keydown", onKey);

      choiceCleanups.push(() => {
        c.removeEventListener("mouseenter", onEnter);
        c.removeEventListener("mouseleave", onLeave);
        c.removeEventListener("focus", onFocus);
        c.removeEventListener("blur", onBlur);
        c.removeEventListener("click", onClick);
        c.removeEventListener("keydown", onKey);
      });
    });

    const onPageShow = (e) => {
      if (e.persisted) {
        st.leaving = false;
        st.alpha = 1;
        setMode("idle");
        setShowSplash(false);
        document.documentElement.classList.add("region-gate-active");
        document.documentElement.classList.remove("india-splash-active");
        if (curtainRef.current) curtainRef.current.classList.remove("show");
        try {
          const last = localStorage.getItem(REGION_STORAGE_KEY);
          if (last === "india" || last === "in") {
            setSuggestion({ region: "india", label: "Last visited" });
          } else if (last === "world" || last === "row") {
            setSuggestion({ region: "world", label: "Last visited" });
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      cancelAnimationFrame(rafReady);
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(idleTimer);
      timers.forEach((t) => clearTimeout(t));

      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("resize", resize);
      resizeObserver.disconnect();

      if (mqlTheme.removeEventListener) {
        mqlTheme.removeEventListener("change", onThemeChange);
      } else if (mqlTheme.removeListener) {
        mqlTheme.removeListener(onThemeChange);
      }

      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      canvas.removeEventListener("pointerleave", onPointerLeave);

      window.removeEventListener("keydown", onWindowKey);
      choiceCleanups.forEach((fn) => fn());

      document.documentElement.classList.remove("region-gate-active");
    };
  }, [navigate]);

  return (
    <div
      ref={rootRef}
      className={`rg-globe-picker ${isReady ? "ready" : "intro"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rg-title"
    >
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <symbol id="mark" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <circle cx="12" cy="12" r="3" fill="currentColor" />
        </symbol>
        <symbol id="arrow" viewBox="0 0 24 24">
          <path
            d="M5 12h14M13 6l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </symbol>
      </svg>

      <main className="shell">
        <section className="copy">
          <div className="brand">
            <img src="/favicon.ico" alt="" width="28" height="28" />
            Praesidio
          </div>
          <h1 id="rg-title">Choose your region</h1>
          <p className="lede">
            Praesidio Studio works with clients across India. Praesidio Care is our engineering lab
            for global missions and partners.
          </p>

          <ul className="choices">
            <li>
              <a
                ref={indiaChoiceRef}
                className="choice"
                data-region="india"
                href="#"
                aria-label="Enter India: Praesidio Studio"
              >
                <div>
                  <div className="region">
                    India{" "}
                    <span
                      className="tag"
                      hidden={suggestion?.region !== "india"}
                    >
                      {suggestion?.region === "india" ? suggestion.label : "Suggested"}
                    </span>
                  </div>
                  <div className="meta">
                    <b>Praesidio Studio</b>
                    <span>India studio</span>
                  </div>
                </div>
                <span className="go" aria-hidden="true">
                  <svg>
                    <use href="#arrow" />
                  </svg>
                </span>
              </a>
            </li>

            <li>
              <a
                ref={worldChoiceRef}
                className="choice"
                data-region="world"
                href="/"
                aria-label="Enter Rest of the world: Praesidio Care, San Francisco and New York"
              >
                <div>
                  <div className="region">
                    Rest of the world{" "}
                    <span
                      className="tag"
                      hidden={suggestion?.region !== "world"}
                    >
                      {suggestion?.region === "world" ? suggestion.label : "Suggested"}
                    </span>
                  </div>
                  <div className="meta">
                    <b>Praesidio Care</b>
                    <span>San Francisco and New York</span>
                  </div>
                </div>
                <span className="go" aria-hidden="true">
                  <svg>
                    <use href="#arrow" />
                  </svg>
                </span>
              </a>
            </li>
          </ul>

          <p className="foot">
            You can switch anytime from the header. Press <kbd>1</kbd> or <kbd>2</kbd> to pick.
          </p>
        </section>

        <div className="stage" ref={stageRef} aria-hidden="true">
          <div className="brand brand-m">
            <img src="/favicon.ico" alt="" width="28" height="28" />
            Praesidio
          </div>
          <canvas id="globe" ref={canvasRef} />
          <div className="hint" id="hint" ref={hintRef}>
            Drag to spin. Tap India or anywhere else.
          </div>
          <div className="readout" id="readout" ref={readoutRef}>
            <strong id="ro-name" ref={roNameRef}>
              Earth
            </strong>
            <span id="ro-coord" ref={roCoordRef}>
              20.00° N, 78.00° E
            </span>
          </div>
        </div>
      </main>

      <div className="curtain" id="curtain" ref={curtainRef} role="status" aria-live="polite">
        <div>
          <h2 id="cu-title" ref={cuTitleRef}>
            Praesidio Studio
          </h2>
          <div className="loader">
            <i />
          </div>
          <p id="cu-sub" ref={cuSubRef}>
            Taking you there
          </p>
        </div>
      </div>

      {showSplash && (
        <IndiaSplash onComplete={handleIndiaComplete} />
      )}
    </div>
  );
}
